import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Popover,
  Skeleton,
  Divider,
  Collapse,
  useTheme,
  useMediaQuery,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import BedIcon from '@mui/icons-material/Bed';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import PlaceIcon from '@mui/icons-material/Place';
import DatePicker from '../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import WifiIcon from '@mui/icons-material/Wifi';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import PoolIcon from '@mui/icons-material/Pool';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SpaIcon from '@mui/icons-material/Spa';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import LocalLaundryServiceIcon from '@mui/icons-material/LocalLaundryService';
import BathtubIcon from '@mui/icons-material/Bathtub';
import TvIcon from '@mui/icons-material/Tv';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getHotelInfo, getRoomTypesWithAvailability, getHotelAmenities, getChannelsPrices, getHotelRatePlans } from './nexastayApi';
import { useNexaStayTheme } from './NexaStayThemeContext';
import { useNexaStayBasePath, nexastayPath, resolveNexaStayHotelId } from './useNexaStayBasePath';
import { formatDisplayDate, toISODate, computeRoomPrice, computeRoomPriceFromRateRow } from './nexastayUtils';
import { getChannelImageUrl } from '../utils/staticUtils';
import { getHotelDateFormat } from '../utils/dateUtils';

// Amenity icon mapping (icon class or code -> MUI icon)
const AMENITY_ICON_MAP = {
  'lnr-wifi': WifiIcon,
  'fa-wifi': WifiIcon,
  wifi: WifiIcon,
  'lnr-snowflake': AcUnitIcon,
  'fa-snowflake': AcUnitIcon,
  ac: AcUnitIcon,
  air_conditioning: AcUnitIcon,
  'lnr-drop': PoolIcon,
  pool: PoolIcon,
  'lnr-car': LocalParkingIcon,
  parking: LocalParkingIcon,
  'lnr-food': RestaurantIcon,
  restaurant: RestaurantIcon,
  'lnr-dumbbell': FitnessCenterIcon,
  gym: FitnessCenterIcon,
  fitness: FitnessCenterIcon,
  'lnr-heart-pulse': SpaIcon,
  spa: SpaIcon,
  'lnr-glass': LocalBarIcon,
  bar: LocalBarIcon,
  'lnr-coffee-cup': FreeBreakfastIcon,
  breakfast: FreeBreakfastIcon,
  'lnr-rocket': AirportShuttleIcon,
  shuttle: AirportShuttleIcon,
  airport_shuttle: AirportShuttleIcon,
  'lnr-shirt': LocalLaundryServiceIcon,
  laundry: LocalLaundryServiceIcon,
  'lnr-droplet': BathtubIcon,
  bathtub: BathtubIcon,
  bathroom: BathtubIcon,
  'lnr-screen': TvIcon,
  tv: TvIcon,
  television: TvIcon,
  'lnr-sun': BeachAccessIcon,
  beach: BeachAccessIcon,
  'lnr-briefcase': BusinessCenterIcon,
  business_center: BusinessCenterIcon,
  swimming_pool: PoolIcon,
  room_service: RoomServiceIcon,
  dining: RestaurantIcon,
  entertainment: TvIcon,
  technology: WifiIcon,
  wellness: SpaIcon,
  business: BusinessCenterIcon,
  climate: AcUnitIcon,
  outdoor: BeachAccessIcon,
};

function getAmenityIcon(a) {
  const codeKey = (a.code || '').toLowerCase().replace(/[\s-]+/g, '_');
  const nameKey = (a.name || '').toLowerCase().replace(/[\s-]+/g, '_');
  const iconStr = (a.icon || '').toLowerCase();
  return AMENITY_ICON_MAP[iconStr] || AMENITY_ICON_MAP[codeKey] || AMENITY_ICON_MAP[nameKey] || AMENITY_ICON_MAP[nameKey?.split('_')[0]] || CheckroomIcon;
}

function RoomCardSkeleton({ theme }) {
  const paper = theme?.paper ?? '#2a2a31';
  const bg = theme?.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  return (
    <Card sx={{ mb: 2, overflow: 'hidden', borderRadius: 2, bgcolor: paper }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
        <Skeleton
          variant="rectangular"
          height={220}
          animation="wave"
          sx={{ width: { md: 300 }, flexShrink: 0, bgcolor: bg }}
        />
        <CardContent sx={{ flex: 1, pt: 2 }}>
          <Skeleton width="60%" height={32} animation="wave" sx={{ bgcolor: bg }} />
          <Skeleton width="40%" height={24} animation="wave" sx={{ mt: 1, bgcolor: bg }} />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Skeleton variant="rounded" width={80} height={28} animation="wave" sx={{ bgcolor: bg }} />
            <Skeleton variant="rounded" width={80} height={28} animation="wave" sx={{ bgcolor: bg }} />
          </Stack>
          <Skeleton width="50%" height={24} animation="wave" sx={{ mt: 2, bgcolor: bg }} />
          <Skeleton width="30%" height={40} animation="wave" sx={{ mt: 2, bgcolor: bg }} />
        </CardContent>
      </Box>
    </Card>
  );
}

const FALLBACK_ROOM_IMAGE = 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80';

function RoomTypeImageSlider({ rt, hotel }) {
  const theme = useNexaStayTheme();
  const images = useMemo(() => {
    if (Array.isArray(rt.images) && rt.images.length > 0) return rt.images;
    const single = rt.image_url || hotel?.image_url || FALLBACK_ROOM_IMAGE;
    return [single];
  }, [rt.images, rt.image_url, hotel?.image_url]);
  const [index, setIndex] = useState(0);
  const [imgError, setImgError] = useState(false);
  const current = imgError ? FALLBACK_ROOM_IMAGE : (images[index % images.length]);
  const hasMultiple = images.length > 1;

  useEffect(() => {
    setImgError(false);
  }, [index, current]);

  const goPrev = useCallback((e) => {
    e.stopPropagation();
    setIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);
  const goNext = useCallback((e) => {
    e.stopPropagation();
    setIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.06)',
      }}
    >
      <Box
        component="img"
        src={current}
        alt={rt.name}
        loading="lazy"
        onError={() => setImgError(true)}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          minWidth: '100%',
          minHeight: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          '.room-card-surface:hover &': { transform: 'scale(1.03)' },
        }}
      />
      {hasMultiple && (
        <>
          <IconButton
            size="small"
            onClick={goPrev}
            sx={{
              position: 'absolute',
              left: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.4)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              zIndex: 2,
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={goNext}
            sx={{
              position: 'absolute',
              right: 6,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'rgba(0,0,0,0.4)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              zIndex: 2,
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              position: 'absolute',
              bottom: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
            }}
          >
            {images.map((_, i) => (
              <Box
                key={i}
                onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: i === index ? theme.accent : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

export default function HotelLandingPage() {
  const { hotelId: routeHotelId } = useParams();
  const hotelId = resolveNexaStayHotelId(routeHotelId);
  const basePath = useNexaStayBasePath();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [hotel, setHotel] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [checkIn, setCheckIn] = useState(() => toISODate(new Date()));
  const [checkOut, setCheckOut] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toISODate(d);
  });
  const [occupancy, setOccupancy] = useState({ rooms: 1, adults: 2, children: 0, infants: 0 });

  const [selectedRooms, setSelectedRooms] = useState({});
  const [guestsAnchorEl, setGuestsAnchorEl] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [channelsData, setChannelsData] = useState(null);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channelImageErrors, setChannelImageErrors] = useState(() => new Set());
  const [mealPlanFilter, setMealPlanFilter] = useState('all');
  const [ratePlans, setRatePlans] = useState([]);
  /** When "All meal plans" is selected and we show meal-plan rows: each item is one room selection (room type + rate plan + occupancy). */
  const [selectedMealPlanRooms, setSelectedMealPlanRooms] = useState([]);

  const onCheckInChange = useCallback(
    (iso) => {
      setCheckIn(iso || '');
      if (iso && checkOut && iso >= checkOut) {
        setCheckOut(moment(iso, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD'));
      }
    },
    [checkOut]
  );

  // Restore selection when arriving from checkout "Modify" (URL has check_in, check_out, rooms_json, rate_plan_id)
  useEffect(() => {
    const urlCheckIn = searchParams.get('check_in');
    const urlCheckOut = searchParams.get('check_out');
    const roomsJsonStr = searchParams.get('rooms_json');
    const urlRatePlanId = searchParams.get('rate_plan_id');
    if (urlCheckIn) setCheckIn(urlCheckIn);
    if (urlCheckOut) setCheckOut(urlCheckOut);
    if (roomsJsonStr) {
      try {
        const str = roomsJsonStr.startsWith('%') ? decodeURIComponent(roomsJsonStr) : roomsJsonStr;
        const arr = JSON.parse(str);
        if (Array.isArray(arr) && arr.length > 0) {
          const hasRatePlanIds = arr.some((item) => item.ratePlanId != null);
          if (hasRatePlanIds) {
            setSelectedMealPlanRooms(
              arr.map((item) => ({
                roomTypeId: String(item.roomTypeId ?? item.room_type_id ?? ''),
                ratePlanId: item.ratePlanId != null ? item.ratePlanId : null,
                adults: item.adults ?? 1,
                children: item.children ?? 0,
                infants: item.infants ?? 0,
              }))
            );
            setSelectedRooms({});
          } else {
            const byType = {};
            arr.forEach((item) => {
              const id = String(item.roomTypeId ?? item.room_type_id ?? '');
              if (!id) return;
              if (!byType[id]) byType[id] = [];
              const idx = item.roomIndex ?? item.room_index ?? byType[id].length;
              byType[id][idx] = {
                adults: item.adults ?? 1,
                children: item.children ?? 0,
                infants: item.infants ?? 0,
              };
            });
            Object.keys(byType).forEach((id) => {
              const a = byType[id];
              for (let i = 0; i < a.length; i++) {
                if (a[i] == null) a[i] = { adults: 1, children: 0, infants: 0 };
              }
            });
            setSelectedRooms(byType);
            setSelectedMealPlanRooms([]);
          }
        }
      } catch {
        // ignore invalid rooms_json
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getHotelInfo(hotelId);
        if (!cancelled) setHotel(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load hotel.');
      }
    })();
    return () => { cancelled = true; };
  }, [hotelId]);

  // Page title: "Welcome to {hotel name}" on NexaStay hotel landing
  const defaultTitle = 'RoomNexa | AI Cloud Platform for Hotels';
  useEffect(() => {
    if (hotel?.name) {
      document.title = `Welcome to ${hotel.name}`;
    } else {
      document.title = defaultTitle;
    }
    return () => { document.title = defaultTitle; };
  }, [hotel?.name]);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getHotelAmenities(hotelId);
        if (!cancelled && data?.amenities) setAmenities(data.amenities);
      } catch {
        if (!cancelled) setAmenities([]);
      }
    })();
    return () => { cancelled = true; };
  }, [hotelId]);

  useEffect(() => {
    if (!hotelId) return;
    let cancelled = false;
    getHotelRatePlans(hotelId)
      .then((data) => { if (!cancelled && data?.rate_plans) setRatePlans(data.rate_plans); })
      .catch(() => { if (!cancelled) setRatePlans([]); });
    return () => { cancelled = true; };
  }, [hotelId]);

  const fetchAvailability = useCallback(async () => {
    if (!hotelId || !checkIn || !checkOut) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getRoomTypesWithAvailability(hotelId, checkIn, checkOut, null, true);
      setAvailability(data);
      if (data?.rate_plans?.length) setRatePlans(data.rate_plans);
    } catch (e) {
      const msg = e.response?.data?.detail ?? e.message ?? 'Failed to load availability.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setAvailability(null);
    } finally {
      setLoading(false);
    }
  }, [hotelId, checkIn, checkOut]);

  useEffect(() => {
    if (!hotelId || !checkIn || !checkOut) return;
    fetchAvailability();
  }, [hotelId, checkIn, checkOut, fetchAvailability]);

  useEffect(() => {
    if (!hotelId || !checkIn) return;
    let cancelled = false;
    const dateForChannels = checkIn;
    setLoadingChannels(true);
    setChannelsData(null);
    setChannelImageErrors(new Set());
    getChannelsPrices(hotelId, dateForChannels)
      .then((data) => { if (!cancelled) setChannelsData(data); })
      .catch(() => { if (!cancelled) setChannelsData({ channels: [], date: dateForChannels }); })
      .finally(() => { if (!cancelled) setLoadingChannels(false); });
    return () => { cancelled = true; };
  }, [hotelId, checkIn]);

  const handleSelectRoom = (roomTypeId, count) => {
    if (count < 0) return;
    setSelectedRooms((prev) => {
      const arr = prev[roomTypeId] || [];
      if (count === 0) {
        const next = { ...prev };
        delete next[roomTypeId];
        return next;
      }
      if (count > arr.length) {
        const toAdd = count - arr.length;
        const newRooms = [...arr, ...Array(toAdd).fill(null).map(() => ({ adults: 1, children: 0, infants: 0 }))];
        return { ...prev, [roomTypeId]: newRooms };
      }
      return { ...prev, [roomTypeId]: arr.slice(0, count) };
    });
  };

  const handleRoomOccupancy = (roomTypeId, roomIndex, key, delta) => {
    setSelectedRooms((prev) => {
      const arr = [...(prev[roomTypeId] || [])];
      const room = arr[roomIndex] || { adults: 1, children: 0, infants: 0 };
      const limits = { adults: [1, 9], children: [0, 9], infants: [0, 4] };
      const [min, max] = limits[key] || [0, 9];
      const cur = room[key] ?? (key === 'adults' ? 1 : 0);
      const next = Math.min(max, Math.max(min, cur + delta));
      arr[roomIndex] = { ...room, [key]: next };
      return { ...prev, [roomTypeId]: arr };
    });
  };

  const getDefaultRoomOccupancy = useCallback((rt) => {
    const requestedRooms = Math.max(1, Number(occupancy.rooms) || 1);
    const maxGuests = Math.max(1, Number(rt?.max_guests ?? rt?.capacity ?? 9) || 9);
    let adults = Math.max(1, Math.ceil((Number(occupancy.adults) || 1) / requestedRooms));
    let children = Math.max(0, Math.ceil((Number(occupancy.children) || 0) / requestedRooms));
    if (adults > maxGuests) adults = maxGuests;
    if (adults + children > maxGuests) children = Math.max(0, maxGuests - adults);
    return { adults, children, infants: 0 };
  }, [occupancy.adults, occupancy.children, occupancy.rooms]);

  const addMealPlanSelection = useCallback((rt, ratePlanId) => {
    if (!ratePlanId) return;
    if (rt?.is_sold_out || (rt?.available_rooms != null && Number(rt.available_rooms) <= 0)) return;
    const defaultOccupancy = getDefaultRoomOccupancy(rt);
    setSelectedMealPlanRooms((prev) => [
      ...prev,
      {
        roomTypeId: rt.id,
        ratePlanId,
        ...defaultOccupancy,
      },
    ]);
  }, [getDefaultRoomOccupancy]);

  /** Remove one room of the given (roomTypeId, ratePlanId) from selection (last added). */
  const removeOneMealPlanSelection = useCallback((roomTypeId, ratePlanId) => {
    setSelectedMealPlanRooms((prev) => {
      const idx = prev.map((r, i) => (String(r.roomTypeId) === String(roomTypeId) && String(r.ratePlanId) === String(ratePlanId) ? i : -1)).filter((i) => i >= 0).pop();
      if (idx == null) return prev;
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const removeMealPlanSelection = useCallback((index) => {
    setSelectedMealPlanRooms((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /** Count of rooms selected for (roomTypeId, ratePlanId). */
  const getMealPlanSelectionCount = useCallback((roomTypeId, ratePlanId) => {
    return selectedMealPlanRooms.filter(
      (r) => String(r.roomTypeId) === String(roomTypeId) && String(r.ratePlanId) === String(ratePlanId)
    ).length;
  }, [selectedMealPlanRooms]);

  /** Total rooms selected for a room type (across all meal plans). */
  const getRoomTypeSelectionCount = useCallback((roomTypeId) => {
    return selectedMealPlanRooms.filter((r) => String(r.roomTypeId) === String(roomTypeId)).length;
  }, [selectedMealPlanRooms]);

  const nights = moment(checkOut).diff(moment(checkIn), 'days');
  const roomTypes = availability?.room_types || [];
  const requestedRooms = Math.max(1, Number(occupancy.rooms) || 1);
  const requestedGuests = Math.max(1, Number(occupancy.adults) || 1) + Math.max(0, Number(occupancy.children) || 0);
  const roomTypeMatchesSearch = useCallback(
    (rt) => {
      const maxGuestsPerRoom = Number(rt?.max_guests ?? rt?.capacity ?? 0);
      if (maxGuestsPerRoom > 0 && requestedGuests > maxGuestsPerRoom * requestedRooms) {
        return false;
      }
      const availableRooms = Number(rt?.available_rooms ?? 0);
      if (availableRooms > 0 && availableRooms < requestedRooms) {
        return false;
      }
      const rateRows = Array.isArray(rt?.rate_plan_rates) ? rt.rate_plan_rates : [];
      const defaultOccupancy = getDefaultRoomOccupancy(rt);
      if (
        rateRows.length > 0 &&
        defaultOccupancy.adults === 1 &&
        !rateRows.some((row) => row.single_occupancy_rate !== null && row.single_occupancy_rate !== undefined)
      ) {
        return false;
      }
      return true;
    },
    [getDefaultRoomOccupancy, requestedGuests, requestedRooms]
  );
  const visibleRoomTypes = useMemo(
    () => roomTypes.filter(roomTypeMatchesSearch),
    [roomTypes, roomTypeMatchesSearch]
  );
  const visibleRoomTypeIds = useMemo(
    () => new Set(visibleRoomTypes.map((rt) => String(rt.id))),
    [visibleRoomTypes]
  );
  useEffect(() => {
    setSelectedMealPlanRooms((prev) =>
      prev.filter((item) => visibleRoomTypeIds.has(String(item.roomTypeId)))
    );
    setSelectedRooms((prev) => {
      const next = {};
      Object.entries(prev || {}).forEach(([roomTypeId, rooms]) => {
        if (visibleRoomTypeIds.has(String(roomTypeId))) next[roomTypeId] = rooms;
      });
      return next;
    });
  }, [visibleRoomTypeIds]);
  const getRoomsForType = (roomTypeId) => selectedRooms[roomTypeId] || [];

  const roomOccupancies = Object.entries(selectedRooms).flatMap(([rtId, rooms]) =>
    (rooms || []).map((occ, i) => ({ roomTypeId: rtId, roomIndex: i, ...occ }))
  );
  const roomTypesMap = (availability?.room_types || []).reduce((m, rt) => ({ ...m, [rt.id]: rt }), {});
  const hasRatePlanRates = visibleRoomTypes.some((rt) => Array.isArray(rt.rate_plan_rates) && rt.rate_plan_rates.length > 0);

  /** When meal-plan-rows mode and user has selected rooms via "Select Room", use this list for count/total/checkout. */
  const useMealPlanSelections = hasRatePlanRates && selectedMealPlanRooms.length > 0;
  const effectiveRoomOccupancies = useMealPlanSelections
    ? selectedMealPlanRooms.map((item, roomIndex) => ({ ...item, roomIndex }))
    : roomOccupancies;

  const totalRooms = effectiveRoomOccupancies.length;
  const totalGuests = effectiveRoomOccupancies.reduce((s, r) => s + (r.adults || 0) + (r.children || 0), 0);
  const symbol = hotel?.currency === 'INR' ? '₹' : hotel?.currency || '₹';
  const taxRate = hotel?.tax_percentage != null ? Number(hotel.tax_percentage) / 100 : 0.18;
  const taxMultiplier = 1 + taxRate;

  let totalWithTax = 0;
  if (useMealPlanSelections) {
    selectedMealPlanRooms.forEach((item) => {
      const rt = roomTypesMap[item.roomTypeId];
      if (!rt?.rate_plan_rates) return;
      const rateRow = rt.rate_plan_rates.find((r) => String(r.rate_plan?.id) === String(item.ratePlanId));
      totalWithTax += computeRoomPriceFromRateRow(rt, rateRow, { adults: item.adults, children: item.children, infants: item.infants }, nights);
    });
    totalWithTax *= taxMultiplier;
  } else {
    roomOccupancies.forEach(({ roomTypeId, adults, children, infants }) => {
      const rt = roomTypesMap[roomTypeId];
      if (rt) totalWithTax += computeRoomPrice(rt, { adults, children, infants }, nights);
    });
    totalWithTax *= taxMultiplier;
  }

  const goToCheckout = () => {
    if (totalRooms === 0) return;
    if (useMealPlanSelections) {
      const roomsPayload = selectedMealPlanRooms.map((item, roomIndex) => ({
        roomTypeId: item.roomTypeId,
        roomIndex,
        ratePlanId: item.ratePlanId,
        adults: item.adults ?? 1,
        children: item.children ?? 0,
        infants: item.infants ?? 0,
      }));
      const ratePlanIds = [...new Set(selectedMealPlanRooms.map((r) => r.ratePlanId).filter(Boolean))];
      const params = new URLSearchParams({
        check_in: checkIn,
        check_out: checkOut,
        rooms: String(totalRooms),
        adults: String(selectedMealPlanRooms.reduce((s, r) => s + (r.adults || 0), 0)),
        children: String(selectedMealPlanRooms.reduce((s, r) => s + (r.children || 0), 0)),
        rooms_json: JSON.stringify(roomsPayload),
      });
      if (ratePlanIds.length === 1) params.set('rate_plan_id', String(ratePlanIds[0]));
      selectedMealPlanRooms.forEach((r) => params.append('room_type', r.roomTypeId));
      navigate(`${nexastayPath(basePath, hotelId, 'checkout')}?${params.toString()}`);
    } else {
      const params = new URLSearchParams({
        check_in: checkIn,
        check_out: checkOut,
        rooms: String(totalRooms),
        adults: String(roomOccupancies.reduce((s, r) => s + (r.adults || 0), 0)),
        children: String(roomOccupancies.reduce((s, r) => s + (r.children || 0), 0)),
        rooms_json: JSON.stringify(roomOccupancies.map(({ roomTypeId, roomIndex, adults, children, infants }) => ({ roomTypeId, roomIndex, adults: adults ?? 1, children: children ?? 0, infants: infants ?? 0 }))),
      });
      if (mealPlanFilter !== 'all') params.set('rate_plan_id', mealPlanFilter);
      roomOccupancies.forEach((r) => params.append('room_type', r.roomTypeId));
      navigate(`${nexastayPath(basePath, hotelId, 'checkout')}?${params.toString()}`);
    }
  };

  const theme = useNexaStayTheme();
  const mainBgSx = { minHeight: '100vh', bgcolor: theme.bg };

  if (!hotelId) {
    return (
      <Box sx={{ ...mainBgSx, p: 3, color: theme.textPrimary }}>
        <Alert severity="warning" sx={{ bgcolor: theme.paper, color: theme.textPrimary, '& .MuiAlert-message': { color: theme.textPrimary }, '& .MuiAlert-icon': { color: '#ff9800' } }}>Missing hotel ID.</Alert>
      </Box>
    );
  }

  if (hotel === null && !error) {
    return (
      <Box sx={{ ...mainBgSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }

  if (error && !hotel) {
    return (
      <Box sx={{ ...mainBgSx, p: 3, color: theme.textPrimary }}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ bgcolor: theme.paper, color: theme.textPrimary, '& .MuiAlert-message': { color: theme.textPrimary }, '& .MuiAlert-icon': { color: '#f44336' } }}>{error}</Alert>
      </Box>
    );
  }

  const searchInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      minHeight: { xs: 56, sm: 64 },
      bgcolor: theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.85)',
      transition: 'all 0.2s ease',
      '& fieldset': {
        border: `2px solid ${theme.borderAccent}`,
        borderRadius: 3,
        transition: 'border-color 0.2s, box-shadow 0.2s',
      },
      '&:hover fieldset': { borderColor: `${theme.accent}66`, boxShadow: `0 0 0 1px ${theme.accent}40` },
      '&.Mui-focused fieldset': {
        borderColor: theme.accent,
        borderWidth: 2,
        boxShadow: `0 0 0 4px ${theme.accent}25`,
      },
      '& input': {
        color: theme.textPrimary,
        py: { xs: 1.5, sm: 2 },
        fontSize: { xs: '0.95rem', sm: '1.1rem' },
        fontWeight: 500,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.textSecondary,
      fontSize: { xs: '0.9rem', sm: '1rem' },
      '&.Mui-focused': { color: theme.accent },
      '&.MuiInputLabel-shrink': { color: theme.accent },
    },
  };

  /* Same as Contact page Check Now bar: dark bar, white text, 58px inputs */
  const accent = theme.accent;
  const bookingStripInputSx = {
    minHeight: { xs: 52, sm: 58 },
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255,255,255,0.06)',
      borderRadius: 0,
      minHeight: { xs: 52, sm: 58 },
      '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
      '&.Mui-focused fieldset': { borderColor: accent },
    },
    '& .MuiInputBase-input': { color: '#f8fafc', fontSize: '0.9rem' },
    '& .MuiInputLabel-root': {
      color: 'rgba(248,250,252,0.86)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontSize: '0.62rem',
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
    '& .MuiSelect-select': { color: '#f8fafc' },
    '& .MuiSvgIcon-root': { color: 'rgba(248,250,252,0.78)' },
    '& .MuiPickersInputBase-root, & .MuiPickersSectionList-root, & .MuiPickersSectionList-sectionContent': { color: '#f8fafc' },
  };
  const buttonTextOnAccent = theme.mode === 'light' ? theme.textPrimary : '#fff';

  return (
    <Box sx={{ ...mainBgSx, color: theme.textPrimary }}>
      <Box sx={{ width: '100%' }}>
        {/* Hero: extends behind fixed header so header sits on bg image */}
        <Box
          sx={{
            position: 'relative',
            marginTop: { xs: -7, sm: -8 },
            minHeight: { xs: 'auto', sm: 600 },
            height: { sm: 600 },
            overflow: 'hidden',
            bgcolor: theme.bg,
          }}
        >
          <Box
            component="img"
            src={hotel?.image_url || hotel?.images?.[0] || 'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1600&q=80'}
            alt={hotel?.name || 'Hotel'}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'blur(4px)',
              transform: 'scale(1.03)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.25)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: 'min(100vh, 680px)', sm: 600 },
              pt: { xs: 10, sm: 12 },
              px: { xs: 2, sm: 3, md: '10%' },
              pb: { xs: 4, sm: 4 },
            }}
          >
            <Chip
              label={hotel?.name ? `Your perfect stay at ${hotel.name} starts here` : 'Your perfect stay starts here'}
              sx={{
                bgcolor: 'rgba(20,22,25,0.78)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: '#f8fafc',
                fontWeight: 700,
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                letterSpacing: '-0.02em',
                px: 2,
                py: 2,
                height: 'auto',
                mb: 3,
                borderRadius: 1.5,
                '& .MuiChip-label': { px: 1 },
              }}
            />
            <Box
              component="form"
              onSubmit={(e) => { e.preventDefault(); fetchAvailability(); }}
              sx={{
                width: '100%',
                maxWidth: 1100,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, minmax(0, 1fr))' },
                gap: { xs: 1, sm: '0.42rem' },
                p: { xs: '0.65rem', sm: '0.7rem' },
                bgcolor: 'rgba(20,22,25,0.78)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 1.5,
                alignItems: 'stretch',
              }}
            >
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Check in"
                  value={checkIn ? moment(checkIn) : null}
                  onChange={(d) => onCheckInChange(d ? d.format('YYYY-MM-DD') : '')}
                  format={getHotelDateFormat()}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true, sx: bookingStripInputSx },
                  }}
                />
                <DatePicker
                  label="Check out"
                  value={checkOut ? moment(checkOut) : null}
                  onChange={(d) => setCheckOut(d ? d.format('YYYY-MM-DD') : '')}
                  minDate={checkIn ? moment(checkIn).add(1, 'day') : undefined}
                  format={getHotelDateFormat()}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true, sx: bookingStripInputSx },
                  }}
                />
              </LocalizationProvider>
              <TextField
                select
                size="small"
                label="Adults"
                value={occupancy.adults ?? 1}
                onChange={(e) => setOccupancy((o) => ({ ...o, adults: Number(e.target.value) }))}
                sx={bookingStripInputSx}
              >
                <MenuItem value={1}>1 Adult</MenuItem>
                <MenuItem value={2}>2 Adults</MenuItem>
                <MenuItem value={3}>3 Adults</MenuItem>
                <MenuItem value={4}>4 Adults</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Children"
                value={occupancy.children ?? 0}
                onChange={(e) => setOccupancy((o) => ({ ...o, children: Number(e.target.value) }))}
                sx={bookingStripInputSx}
              >
                <MenuItem value={0}>Children</MenuItem>
                <MenuItem value={1}>1 Child</MenuItem>
                <MenuItem value={2}>2 Children</MenuItem>
                <MenuItem value={3}>3 Children</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Rooms"
                value={occupancy.rooms ?? 1}
                onChange={(e) => setOccupancy((o) => ({ ...o, rooms: Number(e.target.value) }))}
                sx={bookingStripInputSx}
              >
                <MenuItem value={1}>1 Room</MenuItem>
                <MenuItem value={2}>2 Rooms</MenuItem>
                <MenuItem value={3}>3 Rooms</MenuItem>
                <MenuItem value={4}>4 Rooms</MenuItem>
                <MenuItem value={5}>5 Rooms</MenuItem>
              </TextField>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                sx={{
                  bgcolor: accent,
                  color: buttonTextOnAccent,
                  width: { xs: '100%', md: 'auto' },
                  minHeight: { xs: 48, sm: 52, md: 58 },
                  py: { xs: 1.25, md: 0 },
                  px: { xs: 2, md: 1.5 },
                  borderRadius: 0,
                  textTransform: 'uppercase',
                  letterSpacing: { xs: '0.1em', md: '0.28em' },
                  fontSize: { xs: '0.8rem', sm: '0.75rem', md: '0.72rem' },
                  fontWeight: 600,
                  lineHeight: 1.25,
                  whiteSpace: { xs: 'normal', md: 'nowrap' },
                  alignSelf: 'stretch',
                  '&:hover': { bgcolor: accent, opacity: 0.9 },
                }}
              >
                Check Now
              </Button>
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, sm: 2 }, py: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            {/* Left sidebar: Channels & prices for selected check-in date + Amenities */}
            <Paper
                elevation={0}
                sx={{
                  width: { xs: '100%', md: 300 },
                  flexShrink: 0,
                  height: 'fit-content',
                  position: { md: 'sticky' },
                  top: { md: 72 },
                  order: { xs: 2, md: 0 },
                  borderRadius: 3,
                  overflow: 'hidden',
                  p: 2,
                  bgcolor: theme.paper,
                  border: `1px solid ${theme.borderAccent}`,
                  boxShadow: theme.mode === 'dark' ? '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.04)' : '0 8px 32px rgba(0,0,0,0.12)',
                }}
              >
                <Typography variant="subtitle1" sx={{ color: theme.textPrimary, fontWeight: 700, mb: 2 }}>Channels & prices</Typography>
                <Typography variant="caption" sx={{ color: theme.textMuted, display: 'block', mb: 1.5 }}>{channelsData?.date ? formatDisplayDate(channelsData.date) : (checkIn ? formatDisplayDate(checkIn) : '—')} (check-in date)</Typography>
                {(loadingChannels || (hotelId && channelsData === null)) ? (
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                    <CircularProgress size={20} sx={{ color: theme.accent }} />
                    <Typography variant="body2" sx={{ color: theme.textMuted }}>Loading…</Typography>
                  </Stack>
                ) : channelsData?.channels?.length > 0 ? (
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {channelsData.channels.map((ch) => {
                      const sym = ch.currency === 'INR' ? '₹' : (ch.currency || symbol);
                      const roomTypesList = ch.room_types || [];
                      return (
                        <Box
                          key={ch.id}
                          sx={{
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `1px solid ${theme.borderAccent}`,
                            background: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.6)',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            '&:hover': { borderColor: theme.accent, boxShadow: `0 4px 12px ${theme.accent}30` },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, pt: 1.25, pb: 0.5 }}>
                            {channelImageErrors.has(ch.id) ? (
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 1,
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Typography variant="caption" sx={{ color: theme.textSecondary, fontWeight: 600 }}>
                                  {(ch.name || 'C').charAt(0).toUpperCase()}
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                component="img"
                                src={getChannelImageUrl(ch.image_path)}
                                alt={ch.name}
                                onError={() => setChannelImageErrors((prev) => new Set(prev).add(ch.id))}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                  bgcolor: 'rgba(255,255,255,0.1)',
                                }}
                              />
                            )}
                            <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 600 }} noWrap>{ch.name}</Typography>
                          </Box>
                          {roomTypesList.length > 0 ? (
                            <Stack spacing={0.5} sx={{ px: 1.5, pb: 1.25, pt: 0.25 }}>
                              {roomTypesList.map((rt) => (
                                <Box key={rt.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ color: theme.textSecondary }} noWrap>{rt.name}</Typography>
                                  <Typography variant="caption" sx={{ color: theme.accent, fontWeight: 600 }}>{rt.price != null ? `${sym}${Number(rt.price).toFixed(2)}` : '—'}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Box sx={{ px: 1.5, pb: 1.25 }}>
                              <Typography variant="caption" sx={{ color: theme.textMuted }}>No rates for this date</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.textMuted, mb: 3 }}>No channel prices for selected check-in date.</Typography>
                )}

                <Divider sx={{ borderColor: theme.border, my: 2 }} />
                <Box
                  onClick={() => setAmenitiesOpen((o) => !o)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    py: 0.5,
                    '&:hover': { opacity: 0.9 },
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: theme.textPrimary, fontWeight: 700 }}>
                    Amenities
                  </Typography>
                  <IconButton size="small" sx={{ color: theme.textSecondary, p: 0.25 }}>
                    {amenitiesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </IconButton>
                </Box>
                <Collapse in={amenitiesOpen}>
                  {amenities.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                      {amenities.map((a) => {
                        const IconComponent = getAmenityIcon(a);
                        return (
                          <Chip
                            key={a.id}
                            icon={<IconComponent sx={{ fontSize: 14, color: theme.accent }} />}
                            label={a.name}
                            size="small"
                            sx={{
                              height: 26,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              borderRadius: 1.5,
                              background: `${theme.accent}14`,
                              color: theme.textPrimary,
                              border: `1px solid ${theme.borderAccent}`,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ color: theme.textMuted, display: 'block', mt: 1 }}>No amenities listed.</Typography>
                  )}
                </Collapse>
              </Paper>

              {/* Right content: room types (loaded only on Search); left sidebar always same */}
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0, order: { xs: 1, md: 0 } }}>
                {loading && (
                  <Box sx={{ minHeight: 320 }} role="progressbar" aria-busy aria-label="Loading room types">
                    <Skeleton width={200} height={24} animation="wave" sx={{ bgcolor: theme?.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', mb: 1.5 }} />
                    {[1, 2, 3].map((i) => <RoomCardSkeleton key={i} theme={theme} />)}
                  </Box>
                )}
                {!loading && error && (
                  <Alert severity="warning" sx={{ bgcolor: theme.paper, color: theme.textPrimary, '& .MuiAlert-message': { color: theme.textPrimary }, '& .MuiAlert-icon': { color: '#ff9800' } }} onClose={() => setError(null)}>{error}</Alert>
                )}
                {!loading && !error && roomTypes.length === 0 && (
                  <Paper sx={{ p: 4, textAlign: 'center', bgcolor: theme.paper, border: '1px solid', borderColor: theme.border }}>
                    <MeetingRoomIcon sx={{ fontSize: 48, color: theme.textMuted, mb: 1 }} />
                    <Typography sx={{ color: theme.textSecondary }} variant="body1">No room types for the selected dates.</Typography>
                    <Typography sx={{ color: theme.textMuted, mt: 0.5 }} variant="body2">Choose dates and click Search to check availability.</Typography>
                  </Paper>
                )}
                {!loading && !error && roomTypes.length > 0 && visibleRoomTypes.length === 0 && (
                  <Paper sx={{ p: 4, bgcolor: theme.paper, color: theme.textPrimary, border: `1px solid ${theme.border}`, borderRadius: 2 }}>
                    <Typography variant="h6">No rooms fit the selected guests</Typography>
                    <Typography sx={{ color: theme.textMuted, mt: 0.5 }} variant="body2">
                      Try increasing the number of rooms or reducing adults/children.
                    </Typography>
                  </Paper>
                )}
                {!loading && !error && visibleRoomTypes.length > 0 && (
                  <>
                <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 0, flexShrink: 0 }}>{hotel?.city || 'Hotel'} — {visibleRoomTypes.length} room type{visibleRoomTypes.length !== 1 ? 's' : ''}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                {visibleRoomTypes.map((rt) => {
                  const ratePlanRates = Array.isArray(rt.rate_plan_rates) ? rt.rate_plan_rates : [];
                  const defaultOccupancy = getDefaultRoomOccupancy(rt);
                  const rateRowsForOccupancy = ratePlanRates.filter((row) => {
                    if (defaultOccupancy.adults !== 1) return true;
                    return row.single_occupancy_rate !== null && row.single_occupancy_rate !== undefined;
                  });
                  const showMealPlanRows = hasRatePlanRates && rateRowsForOccupancy.length > 0;
                  const noRoomsLeft =
                    Boolean(rt.is_sold_out)
                    || (rt.available_rooms != null && Number(rt.available_rooms) <= 0);

                  if (showMealPlanRows) {
                    return (
                      <Box
                        key={rt.id}
                        aria-disabled={noRoomsLeft || undefined}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          borderRadius: 2,
                          overflow: 'hidden',
                          bgcolor: theme.cardBg ?? theme.paper,
                          boxShadow: noRoomsLeft ? 'none' : '0 2px 8px rgba(0,0,0,0.2)',
                          border: `1px solid ${noRoomsLeft ? theme.border : `${theme.accent}26`}`,
                          mb: 2,
                          opacity: noRoomsLeft ? 0.48 : 1,
                          filter: noRoomsLeft ? 'grayscale(0.35)' : 'none',
                          pointerEvents: noRoomsLeft ? 'none' : 'auto',
                          transition: 'opacity 0.2s ease, filter 0.2s ease',
                          userSelect: noRoomsLeft ? 'none' : 'auto',
                        }}
                      >
                        <Box sx={{ width: { xs: '100%', md: '38%' }, flexShrink: 0, position: 'relative', minHeight: { xs: 220, md: 320 } }}>
                          <RoomTypeImageSlider rt={rt} hotel={hotel} />
                          {noRoomsLeft && (
                            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                              <Chip label="Sold out" size="medium" sx={{ bgcolor: '#d32f2f', color: '#fff', fontWeight: 600 }} />
                            </Box>
                          )}
                          <Box sx={{ p: 2, borderTop: { xs: `1px solid ${theme.border}`, md: 'none' }, borderRight: { md: `1px solid ${theme.border}` } }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.textPrimary, mb: 0.5 }}>{rt.name}</Typography>
                            {rt.max_guests != null && (
                              <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 0.5 }}>
                                Bedroom 1 — {rt.base_adults === 1 ? '1 King' : `${rt.base_adults != null ? rt.base_adults : 1} bed(s)`} · Max {rt.max_guests} guest(s)
                              </Typography>
                            )}
                            {rt.amenities?.length > 0 && (
                              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                                {rt.amenities.slice(0, 6).map((a, i) => (
                                  <Typography key={i} variant="caption" sx={{ color: theme.textMuted }}>{a.name || a.code || a}</Typography>
                                ))}
                              </Stack>
                            )}
                            <Typography component="a" href="#" variant="caption" sx={{ color: theme.accent, fontWeight: 600, mt: 1, display: 'inline-block' }}>More Details</Typography>
                          </Box>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.border}`, bgcolor: theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle1" sx={{ color: theme.textPrimary, fontWeight: 700 }}>
                                {rt.name}
                              </Typography>
                              {rt.available_rooms != null && (
                                <Chip
                                  size="small"
                                  label={noRoomsLeft ? '0 left' : `${rt.available_rooms} left`}
                                  sx={{
                                    height: 26,
                                    fontWeight: 600,
                                    bgcolor: '#d32f2f',
                                    color: '#fff',
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    '&:hover': { bgcolor: '#b71c1c' },
                                  }}
                                />
                              )}
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <PersonIcon sx={{ color: theme.textSecondary, fontSize: 18 }} />
                                <Typography variant="body2" sx={{ color: theme.textSecondary }}>
                                  Base Adult {rt.base_adults != null ? rt.base_adults : 1}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <GroupWorkIcon sx={{ color: theme.textSecondary, fontSize: 18 }} />
                                <Typography variant="body2" sx={{ color: theme.textSecondary }}>
                                  Max Guest {rt.max_guests ?? rt.capacity ?? '—'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Box>
                          <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 420 }}>
                            {rateRowsForOccupancy.map((row) => {
                              const planName = row.rate_plan?.name || row.rate_plan?.code || `Plan ${row.rate_plan?.id}`;
                              const occupancyTotal = computeRoomPriceFromRateRow(rt, row, defaultOccupancy, nights);
                              const displayRate = nights > 0 ? occupancyTotal / nights : 0;
                              const totalForStay = occupancyTotal;
                              const taxPerNight = displayRate * taxRate;
                              const ratePlanId = row.rate_plan?.id;
                              const count = getMealPlanSelectionCount(rt.id, ratePlanId);
                              const totalForRoomType = getRoomTypeSelectionCount(rt.id);
                              const maxRooms = rt.available_rooms != null ? rt.available_rooms : 99;
                              const atMax = totalForRoomType >= maxRooms;
                              const lineTotal = count > 0 && totalForStay != null ? count * totalForStay : 0;
                              return (
                                <Box
                                  key={row.rate_plan?.id ?? planName}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { sm: 'center' },
                                    justifyContent: 'space-between',
                                    gap: 1.5,
                                    px: 2,
                                    py: 2,
                                    borderBottom: `1px solid ${theme.border}`,
                                    '&:last-child': { borderBottom: 'none' },
                                    bgcolor: !noRoomsLeft && count > 0 ? `${theme.accent}0a` : 'transparent',
                                    borderRadius: 1,
                                    transition: 'background-color 0.2s ease',
                                  }}
                                >
                                  <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.textPrimary }}>{planName}</Typography>
                                      {count > 0 && (
                                        <Chip
                                          label={`${count} room${count > 1 ? 's' : ''} selected`}
                                          size="small"
                                          sx={{
                                            height: 22,
                                            fontWeight: 600,
                                            bgcolor: `${theme.accent}22`,
                                            color: theme.accent,
                                            border: `1px solid ${theme.accent}55`,
                                            '& .MuiChip-label': { px: 1 },
                                          }}
                                        />
                                      )}
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: theme.textMuted }}>Free WiFi · Non-Refundable</Typography>
                                  </Box>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} sx={{ flexShrink: 0 }}>
                                    <Box sx={{ textAlign: 'right' }}>
                                      <Typography variant="body1" sx={{ fontWeight: 700, color: theme.accent }}>{symbol}{totalForStay.toFixed(2)}</Typography>
                                      <Typography variant="caption" sx={{ color: theme.textMuted }}>(+{symbol}{taxPerNight.toFixed(0)} taxes &amp; fees per night)</Typography>
                                      {count > 0 && lineTotal > 0 && (
                                        <Typography variant="caption" sx={{ display: 'block', color: theme.accent, fontWeight: 600, mt: 0.25 }}>
                                          {count} × {symbol}{totalForStay.toFixed(2)} = {symbol}{lineTotal.toFixed(2)}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        borderRadius: 2,
                                        border: `2px solid ${!noRoomsLeft && count > 0 ? theme.accent : theme.border}`,
                                        bgcolor: !noRoomsLeft && count > 0 ? `${theme.accent}14` : (theme.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)'),
                                        overflow: 'hidden',
                                        boxShadow: !noRoomsLeft && count > 0 ? `0 2px 8px ${theme.accent}30` : 'none',
                                        transition: 'all 0.2s ease',
                                      }}
                                    >
                                      <IconButton
                                        size="small"
                                        disabled={noRoomsLeft || count === 0}
                                        onClick={() => removeOneMealPlanSelection(rt.id, ratePlanId)}
                                        sx={{
                                          color: count > 0 ? theme.accent : theme.textMuted,
                                          width: 40,
                                          height: 40,
                                          borderRadius: 0,
                                          '&:hover': { bgcolor: `${theme.accent}22`, color: theme.accent },
                                          '&.Mui-disabled': { color: theme.textMuted },
                                        }}
                                      >
                                        <RemoveIcon sx={{ fontSize: 20 }} />
                                      </IconButton>
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          minWidth: 32,
                                          textAlign: 'center',
                                          fontWeight: 700,
                                          color: count > 0 ? theme.accent : theme.textSecondary,
                                          fontSize: '1rem',
                                        }}
                                      >
                                        {count}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        disabled={noRoomsLeft || atMax}
                                        onClick={() => addMealPlanSelection(rt, ratePlanId)}
                                        sx={{
                                          color: theme.accent,
                                          width: 40,
                                          height: 40,
                                          borderRadius: 0,
                                          '&:hover': { bgcolor: `${theme.accent}22`, color: theme.accent },
                                          '&.Mui-disabled': { color: theme.textMuted },
                                        }}
                                      >
                                        <AddIcon sx={{ fontSize: 20 }} />
                                      </IconButton>
                                    </Box>
                                  </Stack>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    );
                  }

                  return null;
                })}
                </Box>
                  </>
                )}
              </Box>
            </Box>
        </Box>

        {totalRooms > 0 && (
          <Box
            component="footer"
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1100,
              bgcolor: theme.bg,
              borderTop: '2px solid',
              borderColor: `${theme.accent}33`,
              boxShadow: '0 -4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)',
              overflow: 'hidden',
              paddingBottom: 'env(safe-area-inset-bottom, 0)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 1,
                borderTop: `2px solid ${theme.borderAccent}`,
                opacity: 0.5,
              },
            }}
          >
            <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Stack direction="column" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <Chip
                    icon={<CalendarMonthIcon sx={{ color: theme.accent, fontSize: 18 }} />}
                    label={`${formatDisplayDate(checkIn)} – ${formatDisplayDate(checkOut)}`}
                    size="small"
                  sx={{
                    height: 36,
                    fontWeight: 500,
                    bgcolor: `${theme.accent}18`,
                    color: theme.textPrimary,
                    border: `1px solid ${theme.accent}33`,
                    '& .MuiChip-icon': { color: theme.accent },
                  }}
                />
                <Chip
                  icon={<MeetingRoomIcon sx={{ color: theme.accent, fontSize: 18 }} />}
                  label={`${totalRooms} Room${totalRooms > 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    height: 36,
                    fontWeight: 500,
                    bgcolor: `${theme.accent}18`,
                    color: theme.textPrimary,
                    border: `1px solid ${theme.accent}33`,
                    '& .MuiChip-icon': { color: theme.accent },
                  }}
                />
                <Chip
                  icon={<PersonIcon sx={{ color: theme.accent, fontSize: 18 }} />}
                  label={`${totalGuests} Guest${totalGuests > 1 ? 's' : ''}`}
                  size="small"
                  sx={{
                    height: 36,
                    fontWeight: 500,
                    bgcolor: `${theme.accent}18`,
                    color: theme.textPrimary,
                    border: `1px solid ${theme.accent}33`,
                    '& .MuiChip-icon': { color: theme.accent },
                  }}
                />
              </Stack>
              {useMealPlanSelections && selectedMealPlanRooms.length > 0 && (
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ gap: 0.5 }}>
                  {selectedMealPlanRooms.map((item, idx) => {
                    const rt = roomTypesMap[item.roomTypeId];
                    const planName = rt?.rate_plan_rates?.find((r) => String(r.rate_plan?.id) === String(item.ratePlanId))?.rate_plan?.name
                      || rt?.rate_plan_rates?.find((r) => String(r.rate_plan?.id) === String(item.ratePlanId))?.rate_plan?.code
                      || `Plan ${item.ratePlanId}`;
                    const roomName = rt?.name || `Room ${idx + 1}`;
                    return (
                      <Chip
                        key={idx}
                        size="small"
                        label={`${roomName} · ${planName}`}
                        onDelete={() => removeMealPlanSelection(idx)}
                        sx={{
                          maxWidth: '100%',
                          fontWeight: 500,
                          bgcolor: `${theme.accent}14`,
                          color: theme.textPrimary,
                          border: `1px solid ${theme.accent}33`,
                          '& .MuiChip-deleteIcon': { color: theme.textSecondary },
                        }}
                      />
                    );
                  })}
                </Stack>
              )}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={2} justifyContent={{ xs: 'space-between', sm: 'flex-end' }} sx={{ minHeight: { xs: 44 }, flex: { xs: 1, sm: 'none' }, width: { xs: '100%', sm: 'auto' } }}>
                <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  <Typography variant="body2" sx={{ color: theme.textSecondary, lineHeight: 1.2 }}>Total incl. tax</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: theme.accent }}>
                    {symbol}{totalWithTax.toFixed(2)}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={goToCheckout}
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 20 }} />}
                  sx={{
                    bgcolor: theme.accent,
                    color: theme.mode === 'dark' ? '#121212' : '#000',
                    fontWeight: 700,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1.25, sm: 1.5 },
                    minHeight: 44,
                    borderRadius: 2,
                    boxShadow: `0 4px 16px ${theme.accent}59, inset 0 1px 0 rgba(255,255,255,0.25)`,
                    border: `1px solid ${theme.accent}66`,
                    textTransform: 'none',
                    fontSize: '1rem',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      bgcolor: theme.accent,
                      boxShadow: `0 6px 24px ${theme.accent}73, inset 0 1px 0 rgba(255,255,255,0.3)`,
                    },
                  }}
                >
                  Checkout
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
